import { useTranslation } from "react-i18next";

export const useB2BTranslation = () => {
  const { t: originalT, i18n } = useTranslation("b2b");
  const { t: rootT } = useTranslation(); // For root-level translations

  // Helper functions for different B2B sections
  const t = (key: string, options?: any) => originalT(key, options) as string;
  const tDashboard = (key: string) => originalT(`dashboard.${key}`);
  const tAuth = (key: string) => {
    // Use b2b.auth namespace (keys are under b2b.auth.*)
    return originalT(`auth.${key}`);
  };
  const tProfile = (key: string) => originalT(`profile.${key}`);
  const tParticipants = (key: string) => originalT(`participants.${key}`);
  const tParticipantsRoot = (key: string) => rootT(key); // Root-level participant keys
  const tEvents = (key: string) => {
    // If key already starts with 'events.', use it as is, otherwise add the prefix
    const fullKey = key.startsWith('events.') ? key : `events.${key}`;
    return rootT(fullKey);
  };
  const tBrandPerception = (key: string) => {
    // If key already starts with 'brandPerception.', use it as is, otherwise add the prefix
    const fullKey = key.startsWith('brandPerception.') ? key : `brandPerception.${key}`;
    return rootT(fullKey);
  };
  const tCommon = (key: string) => originalT(`common.${key}`);
  const tAdmin = (key: string) => originalT(`admin.${key}`);
  const tButtons = (key: string) => originalT(`buttons.${key}`);

  // Existence checkers — use these to avoid i18next missingKey warnings
  const tDashboardExists = (key: string): boolean =>
    i18n.exists(`dashboard.${key}`, { ns: "b2b" });
  const tProfileExists = (key: string): boolean =>
    i18n.exists(`profile.${key}`, { ns: "b2b" });

  // Helper function to translate business categories
  // Handles comma-separated categories, spaces, and case variations
  const tBusinessCategory = (category: string | null | undefined): string => {
    if (!category) return "";
    
    // If category contains commas, split and translate each part
    if (category.includes(",")) {
      return category
        .split(",")
        .map((cat) => tBusinessCategory(cat.trim()))
        .join(", ");
    }
    
    // Convert "Art and Culture" → "artAndCulture" (proper camelCase)
    const toCamelCase = (str: string): string => {
      const words = str.split(/\s+/);
      if (words.length <= 1) return str.charAt(0).toLowerCase() + str.slice(1);
      return words
        .map((w, i) => i === 0
          ? w.toLowerCase()
          : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
        )
        .join("");
    };

    // Try multiple translation key variations
    const variations = [
      category, // Original (e.g. "artAndCulture")
      category.toLowerCase(), // Lowercase
      category.charAt(0).toUpperCase() + category.slice(1).toLowerCase(), // Capitalized
      toCamelCase(category), // Proper camelCase: "Art and Culture" → "artAndCulture"
      category.replace(/\s+/g, ""), // No spaces
      category.replace(/\s+/g, "").toLowerCase(), // No spaces, lowercase
      category.replace(/\s+/g, "").charAt(0).toLowerCase() + category.replace(/\s+/g, "").slice(1), // camelCase no-space
    ];
    
    // Try each variation — check existence first to avoid i18next missingKey warnings
    for (const variation of variations) {
      const translationKey = `auth.businessCategories.${variation}`;
      if (i18n.exists(translationKey)) {
        const translated = rootT(translationKey);
        if (translated && translated !== translationKey) {
          return translated;
        }
      }
    }
    
    // Fallback: return formatted original value
    return category
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return {
    t,
    tDashboard,
    tAuth,
    tProfile,
    tParticipants,
    tParticipantsRoot,
    tEvents,
    tBrandPerception,
    tCommon,
    tAdmin,
    tButtons,
    tBusinessCategory,
    tDashboardExists,
    tProfileExists,
    i18n,
  };
};


